import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import { FilterInfoType } from '../filter-info/filter-info.interface';
import { Filters } from '../filters/filter.interface';
import { ApiResource } from './../../models/api-resource.interface';
import generateDescription, { GenerateDescriptionState } from './generate-description/generate-description.store';
import { Position, PositionPublication } from './position.interface';
import { PositionStatus } from '../position-statuses/position-status.interface';
import { POSITION_DROPDOWN_INCLUDES, PositionService } from './position.service';
import publish, { PublishState } from './publish/publish.store';
import translate, { TranslateState } from './translate/translate.store';

const service = new PositionService();

export interface PositionState {
   positionsOverview?: ApiResource<Array<Position>>;
   filters?: Filters;
   query?: LocationQuery;
   activeIndex: number;
   targetType?: FilterInfoType;

   // modules?
   publish?: PublishState;
   generateDescription?: GenerateDescriptionState;
   translate?: TranslateState;
}

const PositionStore: Module<PositionState, StoreState> = {
   namespaced: true,
   state: {
      filters: { quick_search: [], filter_elements: [] },
      updateActivities: false,
      activeIndex: 0,
   },
   getters: {
      positionsOverview: (state) => state.positionsOverview,
      filters: (state) => state.filters,
      query: (state) => state.query,
      activeIndex: (state) => state.activeIndex,
   },
   mutations: {
      SET_UPDATE_ACTIVITIES(state, updateActivities: boolean) {
         state.updateActivities = updateActivities;
      },
      SET_POSITIONS_OVERVIEW(state, positionsOverview?: ApiResource<Array<Position>>) {
         state.positionsOverview = positionsOverview;
      },
      SET_QUERY(state, query: LocationQuery) {
         state.query = query;
      },
      UPDATE_POSITION_LIST(state, position: Position) {
         if (state.positionsOverview) {
            const index = state.positionsOverview!.data.findIndex((p) => p.id == position.id);
            if (state.positionsOverview?.data[index]) {
               state.positionsOverview?.data.splice(index, 1, position);
               state.activeIndex = index;
            }
         }
      },
      DELETE_FROM_POSITION_LIST(state, positionIds) {
         state.positionsOverview!.data = state.positionsOverview?.data.filter((p) => !positionIds.includes(p.id)) || [];
      },
      UPDATE_POSITION_PUBLICATION(state, { positionId, publication }: { positionId: number; publication: PositionPublication }) {
         const position = state.positionsOverview?.data.find((p) => p.id === positionId);
         if (position) {
            const index = position.publications.findIndex((p) => p.id === publication.id);
            if (index >= 0) {
               position.publications.splice(index, 1, publication);
            } else {
               position.publications.push(publication);
            }
         }
      },
      BULK_UPDATE_POSITIONS(state, payload: { updatedPositions: Position[]; statuses: PositionStatus[] }) {
         payload.updatedPositions.forEach((updatedPosition: Position) => {
            if (!state.positionsOverview) return;

            const index = state.positionsOverview.data.findIndex((p) => p.id === updatedPosition.id);
            if (index === -1) return;

            state.positionsOverview.data[index] = {
               ...state.positionsOverview.data[index],
               ...updatedPosition,
               status: payload.statuses.find((s: any) => s.id === updatedPosition.status_id)!,
            };
         });
      },
   },
   actions: {
      async getFilteredPositions(
         context,
         { query, filters, targetType }: { query: LocationQuery; filters: Filters; targetType: FilterInfoType },
      ) {
         const positionsOverview = await service.getFilteredPositions(query, filters, targetType);
         return positionsOverview;
      },
      async searchPositions(context, query: LocationQuery) {
         const response = await service.getPositions({
            per_page: '100',
            page: '1',
            include: POSITION_DROPDOWN_INCLUDES.join(','),
            ...query,
         });
         return response.data;
      },
      async autocomplete(context, search: string) {
         return await service.autocomplete(search);
      },
      async createPosition(context, position: Position) {
         const response = await service.postPosition(position);
         return response.data;
      },
      async getPosition(context, payload: { positionId: number; query: LocationQuery }) {
         return await service.getPosition(payload.positionId, payload.query);
      },
      async putPosition(context, position: Position) {
         const response = await service.putPosition(position);
         context.commit('UPDATE_POSITION_LIST', { ...response, elastic_score: position.elastic_score } as Position);
         return response;
      },
      async generateXml(context, positionId: number) {
         return await service.generateXml(positionId);
      },
      async checkDelete(context, payload: { positionId: number; force: boolean }) {
         return await service.checkDeletePosition(payload.positionId, payload.force);
      },
      async deletePosition(context, payload: { positionId: number; force: boolean }) {
         return await service.deletePosition(payload.positionId, payload.force);
      },
      async bulkTakePositionOffline(context, payload: { positions: Position[] }) {
         const statuses = context.rootState.positionStatus?.positionStatuses;
         const updatedPositionsResponse = await service.bulkTakePositionOffline(payload.positions);
         const updatedPositions = updatedPositionsResponse?.data || [];

         context.commit('BULK_UPDATE_POSITIONS', { updatedPositions, statuses });

         return updatedPositionsResponse;
      },
      async bulkUpdatePositionStatus(context, payload: { positions: Position[]; statusId: number }) {
         const statuses = context.rootState.positionStatus?.positionStatuses;
         const updatedPositionsResponse = await service.bulkUpdatePositionStatus(payload.positions, payload.statusId);
         const updatedPositions = updatedPositionsResponse?.data || [];

         context.commit('BULK_UPDATE_POSITIONS', { updatedPositions, statuses });

         return updatedPositionsResponse;
      },
   },
   modules: {
      publish,
      generateDescription,
      translate,
   },
};

export default PositionStore;
